﻿/**
 * Transporte Analytics → gas/analytics-api.
 * En producción (analytics.gicapital.cl) usa POST same-origin /api/gas (Cloud Function).
 * En local, JSONP directo al GAS.
 */
import { ANALYTICS_CONFIG } from '../config/analytics-config.js';

function appOrigin_() {
  try {
    return window.location.origin || 'https://analytics.gicapital.cl';
  } catch (e) {
    return 'https://analytics.gicapital.cl';
  }
}

function unwrapPayload_(payload, action) {
  if (payload == null || typeof payload !== 'object') {
    return {};
  }
  if (payload.success === false) {
    const err = payload.error;
    const msg =
      (err && (err.message || err.code)) ||
      payload.message ||
      payload.reason ||
      'Error en ' + action;
    const e = new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    e.code = err && err.code ? String(err.code) : 'GAS_ERROR';
    e.status = err && err.status ? err.status : 400;
    e.payload = payload;
    throw e;
  }
  if (payload.success === true && Object.prototype.hasOwnProperty.call(payload, 'data')) {
    return payload.data == null ? {} : payload.data;
  }
  return payload;
}

function shouldUseSameOriginGasProxy_() {
  // Proxy Cloud Function aún no está en producción (falló el build SA).
  return false;
}

function buildGasBody_(action, params, idToken) {
  const body = {
    action: String(action || '').trim(),
    origin: appOrigin_()
  };
  if (idToken) body.idToken = idToken;
  Object.keys(params || {}).forEach((key) => {
    const val = params[key];
    if (val === null || val === undefined || val === '') return;
    body[key] = typeof val === 'object' ? JSON.stringify(val) : val;
  });
  if (params && params.turnstileToken && !body['cf-turnstile-response']) {
    body['cf-turnstile-response'] = String(params.turnstileToken);
  }
  return body;
}

export function callGasAction(action, params, idToken, opts) {
  const timeoutMs = (opts && opts.timeoutMs) || 60000;
  const retries = opts && opts.retries != null ? Number(opts.retries) : 0;
  return callGasActionOnce_(action, params, idToken, timeoutMs).catch((err) => {
    const msg = String((err && err.message) || '');
    const network = msg.indexOf('Error de red al llamar analytics-api') >= 0;
    const timeout = msg.indexOf('Tiempo de espera agotado') >= 0;
    if ((!network && !timeout) || retries < 1) throw err;
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        callGasAction(action, params, idToken, { timeoutMs: timeoutMs, retries: retries - 1 })
          .then(resolve)
          .catch(reject);
      }, 800);
    });
  });
}

function callGasActionOnce_(action, params, idToken, timeoutMs) {
  if (shouldUseSameOriginGasProxy_()) {
    return callGasProxyOnce_(action, params, idToken, timeoutMs).catch((err) => {
      console.warn('[analytics] /api/gas no disponible, JSONP directo', err);
      return callGasJsonpOnce_(action, params, idToken, timeoutMs);
    });
  }
  return callGasJsonpOnce_(action, params, idToken, timeoutMs);
}

function callGasProxyOnce_(action, params, idToken, timeoutMs) {
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(() => {
    if (ctrl) ctrl.abort();
  }, timeoutMs);
  const opts = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildGasBody_(action, params, idToken))
  };
  if (ctrl) opts.signal = ctrl.signal;
  return fetch('/api/gas', opts)
    .then(async (res) => {
      const payload = await res.json().catch(() => null);
      if (!payload) {
        throw new Error('Error de red al llamar analytics-api (' + action + ').');
      }
      return unwrapPayload_(payload, action);
    })
    .catch((err) => {
      if (err && err.name === 'AbortError') {
        throw new Error(
          'Tiempo de espera agotado al consultar ' +
            action +
            '. Si acabas de entrar, reintenta; si persiste, revisa analytics-api.'
        );
      }
      if (err && err.message && String(err.message).indexOf('analytics-api') >= 0) throw err;
      if (err && err.code) throw err;
      throw new Error('Error de red al llamar analytics-api (' + action + ').');
    })
    .finally(() => {
      clearTimeout(timer);
    });
}

function callGasJsonpOnce_(action, params, idToken, timeoutMs) {
  return new Promise((resolve, reject) => {
    const cbName = '__gicAnalyticsCb_' + Date.now() + '_' + Math.floor(Math.random() * 1e6);
    let settled = false;
    let script = null;

    const finish = (err, payload) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window[cbName] = function () {};
      if (script && script.parentNode) {
        try {
          script.parentNode.removeChild(script);
        } catch (e) {
          /* ignore */
        }
      }
      if (err) {
        reject(err);
        return;
      }
      try {
        resolve(unwrapPayload_(payload, action));
      } catch (unwrapErr) {
        reject(unwrapErr);
      }
    };

    const timer = setTimeout(() => {
      finish(
        new Error(
          'Tiempo de espera agotado al consultar ' +
            action +
            '. Si acabas de entrar, reintenta; si persiste, revisa analytics-api.'
        )
      );
    }, timeoutMs);

    window[cbName] = function (payload) {
      finish(null, payload);
    };

    const q = new URLSearchParams();
    q.set('action', String(action || '').trim());
    q.set('callback', cbName);
    q.set('origin', appOrigin_());
    if (idToken) q.set('idToken', idToken);

    Object.keys(params || {}).forEach((key) => {
      const val = params[key];
      if (val === null || val === undefined || val === '') return;
      q.set(key, String(val));
    });

    // Alias Cloudflare / portal
    if (params && params.turnstileToken && !q.has('cf-turnstile-response')) {
      q.set('cf-turnstile-response', String(params.turnstileToken));
    }

    script = document.createElement('script');
    script.async = true;
    script.src = ANALYTICS_CONFIG.scriptUrl + '?' + q.toString();
    script.onerror = function () {
      finish(new Error('Error de red al llamar analytics-api (' + action + ').'));
    };
    (document.body || document.head).appendChild(script);
  });
}

export function fetchHealth() {
  return callGasAction('health', {}, null, { timeoutMs: 12000 });
}

export function fetchModuleInfo() {
  return callGasAction('getModuleInfo', {}, null, { timeoutMs: 12000 });
}

export function fetchSalesAnalyticsPermissions(idToken) {
  return callGasAction('getSalesAnalyticsPermissions', {}, idToken, {
    timeoutMs: 90000,
    retries: 2
  });
}

export function fetchSalesAnalytics(filters, idToken) {
  const params = {
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    sellerId: filters.sellerId || '',
    pipelineId: filters.pipelineId || ''
  };
  if (filters && filters.mode) params.mode = String(filters.mode);
  if (Array.isArray(filters.originTags)) {
    // JSON incluso si [] — distingue “ninguna etiqueta” de “sin filtro”.
    params.originTags = JSON.stringify(filters.originTags);
  } else if (filters.originTags != null && filters.originTags !== '') {
    params.originTags = String(filters.originTags);
  }
  const timeoutMs = filters && filters.mode === 'cohort' ? 90000 : 180000;
  return callGasAction('getSalesAnalytics', params, idToken, {
    timeoutMs: timeoutMs,
    retries: 1
  });
}

/**
 * Asistente de ayuda Analytics (explicación de métricas).
 * @param {{ pregunta: string, historial?: Array<{role:string,text:string}>, contexto?: Object }} opts
 * @param {string} idToken
 */
export function fetchAnalyticsAsistente(opts, idToken) {
  const o = opts || {};
  const historial = Array.isArray(o.historial) ? o.historial.slice(-4) : [];
  const contexto = o.contexto && typeof o.contexto === 'object' ? o.contexto : {};
  return callGasAction(
    'analyticsAsistenteConsulta',
    {
      pregunta: String(o.pregunta || '').slice(0, 600),
      historialJson: JSON.stringify(
        historial.map((h) => ({
          role: h && h.role === 'assistant' ? 'assistant' : 'user',
          text: String((h && h.text) || '').slice(0, 280)
        }))
      ),
      contextoJson: JSON.stringify({
        routeId: String(contexto.routeId || '').slice(0, 80),
        periodLabel: String(contexto.periodLabel || '').slice(0, 80),
        dateFrom: String(contexto.dateFrom || '').slice(0, 12),
        dateTo: String(contexto.dateTo || '').slice(0, 12),
        hint: String(contexto.hint || '').slice(0, 240)
      })
    },
    idToken,
    { timeoutMs: 90000 }
  );
}

export function syncKommoLeads(idToken, opts) {
  const tipo = (opts && opts.tipo) || 'full';
  return callGasAction(
    'syncKommoToSheet',
    {
      tipo: tipo,
      completeScan: opts && opts.completeScan === true ? '1' : '',
      turnstileToken: (opts && opts.turnstileToken) || ''
    },
    idToken,
    { timeoutMs: 360000 }
  );
}

export function fetchStageConfig(idToken, opts) {
  const sync = opts && opts.syncEmbudos ? '1' : '';
  return callGasAction(
    'getSalesAnalyticsStageConfig',
    { syncEmbudos: sync },
    idToken,
    { timeoutMs: 120000 }
  );
}

export function fetchOriginTags(idToken, opts) {
  const sync = opts && opts.syncFromLeads ? '1' : '';
  return callGasAction(
    'getSalesAnalyticsOriginTags',
    { syncFromLeads: sync },
    idToken,
    { timeoutMs: 120000 }
  );
}

export function fetchMeetingStages(idToken) {
  return callGasAction('getSalesAnalyticsMeetingStages', {}, idToken, {
    timeoutMs: 90000
  });
}

export function saveMeetingStages(stages, idToken, opts) {
  const list = Array.isArray(stages) ? stages : [];
  return callGasAction(
    'saveSalesAnalyticsMeetingStages',
    {
      stagesJson: JSON.stringify(list),
      turnstileToken: (opts && opts.turnstileToken) || ''
    },
    idToken,
    { timeoutMs: 90000 }
  );
}

export function fetchLeadProbe(query, idToken) {
  return callGasAction(
    'getSalesAnalyticsLeadProbe',
    { q: String(query || '').trim() },
    idToken,
    { timeoutMs: 90000 }
  );
}

export function fetchLeadTrace(leadId, idToken) {
  return callGasAction('getSalesAnalyticsLeadTrace', { leadId: String(leadId || '') }, idToken, { timeoutMs: 120000 });
}

export function fetchLeadsInventory(params, idToken) {
  const p = params || {};
  return callGasAction(
    'getSalesAnalyticsLeadsInventory',
    {
      dateFrom: p.dateFrom || '',
      dateTo: p.dateTo || '',
      q: p.q || '',
      tagFilter: p.tagFilter || 'all',
      limit: p.limit != null ? String(p.limit) : '200',
      offset: p.offset != null ? String(p.offset) : '0'
    },
    idToken,
    { timeoutMs: 120000 }
  );
}

export function fetchContactsInventory(params, idToken) {
  const p = params || {};
  const tags = Array.isArray(p.tags) ? p.tags : [];
  return callGasAction(
    'getSalesAnalyticsContactsInventory',
    {
      dateFrom: p.dateFrom || '',
      dateTo: p.dateTo || '',
      applyDate: p.applyDate != null ? String(p.applyDate) : '1',
      q: p.q || '',
      vinculo: p.vinculo || 'all',
      hasLeads: p.hasLeads || 'all',
      hasGic: p.hasGic || 'all',
      tagsJson: tags.length ? JSON.stringify(tags) : '',
      sortBy: p.sortBy || 'createdAt',
      sortDir: p.sortDir || 'desc',
      limit: p.limit != null ? String(p.limit) : '500',
      offset: p.offset != null ? String(p.offset) : '0'
    },
    idToken,
    { timeoutMs: 120000 }
  );
}

/**
 * Cruza filas del CSV Meta Lead Ads con el espejo Kommo.
 * @param {{ leads: Array<Object> }} params
 * @param {string} idToken
 */
export function matchMetaLeadsCsv(params, idToken) {
  const leads = Array.isArray(params && params.leads) ? params.leads : [];
  return callGasAction(
    'matchMetaLeadsCsv',
    { leadsJson: JSON.stringify(leads) },
    idToken,
    { timeoutMs: 120000 }
  );
}

/**
 * Embudos + etapas Kommo para elegir destino del import CSV Meta.
 * @param {string} idToken
 */
export function listMetaCsvKommoPipelines(idToken) {
  return callGasAction('listMetaCsvKommoPipelines', {}, idToken, { timeoutMs: 60000 });
}

/**
 * Crea en Kommo los leads seleccionados del CSV Meta.
 * @param {{ leads: Array<Object>, leadTag?: string, tags?: string[], includeCampaignTag?: boolean, pipelineId?: string, statusId?: string, pipelineName?: string, statusName?: string }} params
 * @param {string} idToken
 */
export function pushMetaLeadsToKommo(params, idToken) {
  const leads = Array.isArray(params && params.leads) ? params.leads : [];
  const tags = Array.isArray(params && params.tags)
    ? params.tags.map((t) => String(t == null ? '' : t).trim()).filter(Boolean)
    : [];
  const includeCampaignTag =
    params && params.includeCampaignTag === false ? '0' : '1';
  return callGasAction(
    'pushMetaLeadsToKommo',
    {
      leadsJson: JSON.stringify(leads),
      leadTag: (params && params.leadTag) || (tags[0] || ''),
      tagsJson: tags.length ? JSON.stringify(tags) : '',
      includeCampaignTag,
      pipelineId: (params && params.pipelineId) || '',
      statusId: (params && params.statusId) || '',
      pipelineName: (params && params.pipelineName) || '',
      statusName: (params && params.statusName) || ''
    },
    idToken,
    { timeoutMs: 180000 }
  );
}

export function fetchCampaignRecovery(params, idToken) {
  const p = params || {};
  return callGasAction(
    'getSalesAnalyticsCampaignRecovery',
    {
      dateFrom: p.dateFrom || '',
      dateTo: p.dateTo || '',
      campaignId: p.campaignId || '',
      campaignName: p.campaignName || '',
      webCampana: p.webCampana || p.campana || ''
    },
    idToken,
    { timeoutMs: 180000 }
  );
}

async function fetchMetaLeadsExportFromApi(params, idToken, opts) {
  const p = params || {};
  const base = String(ANALYTICS_CONFIG.apiBase || 'https://api.gicapital.cl').replace(/\/$/, '');
  const qs = new URLSearchParams();
  if (p.dateFrom && p.dateTo) {
    qs.set('from', String(p.dateFrom));
    qs.set('to', String(p.dateTo));
  } else {
    qs.set('days', '15');
  }
  const external = opts && opts.signal;
  const ctrl = new AbortController();
  const onExternalAbort = () => {
    try {
      ctrl.abort();
    } catch (e) {
      /* ignore */
    }
  };
  if (external) {
    if (external.aborted) ctrl.abort();
    else external.addEventListener('abort', onExternalAbort, { once: true });
  }
  const t = setTimeout(() => ctrl.abort(), 60_000);
  try {
    const res = await fetch(`${base}/monitor/meta-leads-export?${qs.toString()}`, {
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + String(idToken || '')
      },
      signal: ctrl.signal
    });
    const body = await res.json().catch(() => null);
    if (res.status === 401 || res.status === 403) {
      const err = new Error('unauthorized');
      err.httpStatus = res.status;
      throw err;
    }
    if (!res.ok) {
      const err = new Error((body && (body.error || body.message)) || 'http_' + res.status);
      err.httpStatus = res.status;
      throw err;
    }
    return body && typeof body === 'object' ? body : { ok: false, leads: [] };
  } finally {
    clearTimeout(t);
    if (external) external.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * Extrae Instant Forms del período vía gicapital-api.
 * GAS queda de respaldo si el API falla. No crea leads en Kommo.
 * @param {{ dateFrom: string, dateTo: string, campaignId?: string, campaignName?: string }} params
 * @param {string} idToken
 * @param {{ signal?: AbortSignal }} [opts]
 */
export async function fetchMetaLeadAdsContacts(params, idToken, opts) {
  const p = params || {};
  try {
    const apiRes = await fetchMetaLeadsExportFromApi(p, idToken, opts);
    if (apiRes && apiRes.ok === true) return apiRes;
    if (apiRes && Array.isArray(apiRes.leads) && apiRes.leads.length) return apiRes;
  } catch (e) {
    if (e && e.name === 'AbortError') throw e;
    /* fallback GAS */
  }
  if (opts && opts.signal && opts.signal.aborted) {
    const err = new Error('Aborted');
    err.name = 'AbortError';
    throw err;
  }
  return callGasAction(
    'getMetaLeadAdsContacts',
    {
      dateFrom: p.dateFrom || '',
      dateTo: p.dateTo || '',
      campaignId: p.campaignId || '',
      campaignName: p.campaignName || ''
    },
    idToken,
    { timeoutMs: 300000 }
  );
}

/**
 * Leads del espejo Kommo creados en el período.
 * @param {{ dateFrom: string, dateTo: string }} params
 * @param {string} idToken
 */
export function fetchKommoLeadsInRange(params, idToken) {
  const p = params || {};
  return callGasAction(
    'getKommoLeadsInRange',
    {
      dateFrom: p.dateFrom || '',
      dateTo: p.dateTo || ''
    },
    idToken,
    { timeoutMs: 120000, retries: 1 }
  );
}

/**
 * Diagnósticos Web del período (landing / pixel). Contactos con nombre-teléfono.
 * @param {{ dateFrom: string, dateTo: string }} params
 * @param {string} idToken
 */
export function fetchWebLeadsInRange(params, idToken) {
  const p = params || {};
  return callGasAction(
    'getWebLeadsInRange',
    {
      dateFrom: p.dateFrom || '',
      dateTo: p.dateTo || ''
    },
    idToken,
    { timeoutMs: 120000, retries: 1 }
  );
}

export function saveOriginTags(tags, idToken, opts) {
  return callGasAction(
    'saveSalesAnalyticsOriginTags',
    {
      tagsJson: JSON.stringify(Array.isArray(tags) ? tags : []),
      turnstileToken: (opts && opts.turnstileToken) || ''
    },
    idToken,
    { timeoutMs: 90000 }
  );
}

export async function saveStageConfig(stages, idToken, opts) {
  const onProgress = (opts && opts.onProgress) || function () {};
  const turnstileToken = (opts && opts.turnstileToken) || '';
  const internalStates = (opts && Array.isArray(opts.internalStates) && opts.internalStates) || null;
  const stateGroups = (opts && Array.isArray(opts.stateGroups) && opts.stateGroups) || null;
  const list = Array.isArray(stages) ? stages : [];
  const CHUNK = 4;

  onProgress('Guardando estados internos…');
  const clearParams = { mode: 'clear', turnstileToken: turnstileToken };
  if (internalStates) clearParams.statesJson = JSON.stringify(internalStates);
  if (stateGroups) clearParams.groupsJson = JSON.stringify(stateGroups);
  const clearRes = await callGasAction(
    'saveSalesAnalyticsStageConfig',
    clearParams,
    idToken,
    { timeoutMs: 90000 }
  );
  if (!clearRes || clearRes.success === false) {
    throw new Error((clearRes && clearRes.error) || 'No se pudo limpiar Sales_Stage_Map.');
  }

  let saved = 0;
  const total = list.length;
  for (let i = 0; i < list.length; i += CHUNK) {
    const chunk = list.slice(i, i + CHUNK);
    onProgress('Guardando ' + Math.min(i + CHUNK, total) + ' / ' + total + '…');
    const res = await callGasAction(
      'saveSalesAnalyticsStageConfig',
      {
        mode: 'append',
        stagesJson: JSON.stringify(chunk)
      },
      idToken,
      { timeoutMs: 90000 }
    );
    if (!res || res.success === false) {
      throw new Error(
        (res && res.error) ||
          'Error al guardar lote de etapas (' + (i + 1) + '–' + (i + chunk.length) + ').'
      );
    }
    saved += Number(res.saved || chunk.length) || 0;
  }

  onProgress('Recargando…');
  const finalList = await fetchStageConfig(idToken);
  return {
    success: true,
    saved: saved,
    sheetName: (finalList && finalList.sheetName) || 'Sales_Stage_Map',
    items: (finalList && finalList.items) || list,
    internalStates: (finalList && finalList.internalStates) || internalStates || null,
    stateGroups: (finalList && finalList.stateGroups) || stateGroups || null,
    message: 'Guardado: ' + saved + ' etapa(s) en lotes.',
    generatedAt: new Date().toISOString()
  };
}

function forceFlag_(filters) {
  return filters && (filters.force === true || filters.force === '1') ? '1' : '';
}

export function fetchMarketingAnalytics(filters, idToken) {
  return callGasAction(
    'getMarketingAnalytics',
    {
      dateFrom: (filters && filters.dateFrom) || '',
      dateTo: (filters && filters.dateTo) || '',
      force: forceFlag_(filters)
    },
    idToken,
    { timeoutMs: 120000 }
  );
}

export function fetchIntegrationsStatus(idToken) {
  return callGasAction('getIntegrationsStatus', {}, idToken, { timeoutMs: 45000 });
}

export function fetchMetaAdsDiag(idToken) {
  return callGasAction('diagMetaAds', {}, idToken, { timeoutMs: 30000 });
}

export function fetchReservationsAnalytics(filters, idToken) {
  return callGasAction(
    'getReservationsAnalytics',
    {
      dateFrom: (filters && filters.dateFrom) || '',
      dateTo: (filters && filters.dateTo) || '',
      executiveId: (filters && filters.executiveId) || '',
      projectId: (filters && filters.projectId) || '',
      status: (filters && filters.status) || '',
      force: forceFlag_(filters)
    },
    idToken,
    { timeoutMs: 120000 }
  );
}

export function fetchProjectsAnalytics(filters, idToken) {
  return callGasAction(
    'getProjectsAnalytics',
    {
      dateFrom: (filters && filters.dateFrom) || '',
      dateTo: (filters && filters.dateTo) || '',
      projectId: (filters && filters.projectId) || '',
      status: (filters && filters.status) || '',
      force: forceFlag_(filters)
    },
    idToken,
    { timeoutMs: 120000 }
  );
}

export function fetchCommercialAnalytics(filters, idToken) {
  return callGasAction(
    'getCommercialAnalytics',
    {
      dateFrom: (filters && filters.dateFrom) || '',
      dateTo: (filters && filters.dateTo) || '',
      executiveId: (filters && filters.executiveId) || '',
      sellerId: (filters && filters.executiveId) || '',
      force: forceFlag_(filters)
    },
    idToken,
    { timeoutMs: 180000 }
  );
}

export function fetchExecutivesAnalytics(filters, idToken) {
  return callGasAction(
    'getExecutivesAnalytics',
    {
      dateFrom: (filters && filters.dateFrom) || '',
      dateTo: (filters && filters.dateTo) || '',
      force: forceFlag_(filters)
    },
    idToken,
    { timeoutMs: 180000 }
  );
}

export function fetchExecutiveOverview(filters, idToken) {
  return callGasAction(
    'getExecutiveOverview',
    {
      dateFrom: (filters && filters.dateFrom) || '',
      dateTo: (filters && filters.dateTo) || '',
      force: forceFlag_(filters)
    },
    idToken,
    { timeoutMs: 180000 }
  );
}

export function fetchMeetingsAnalytics(filters, idToken) {
  return callGasAction(
    'getMeetingsAnalytics',
    {
      dateFrom: (filters && filters.dateFrom) || '',
      dateTo: (filters && filters.dateTo) || '',
      executiveId: (filters && filters.executiveId) || '',
      status: (filters && filters.status) || '',
      force: forceFlag_(filters)
    },
    idToken,
    { timeoutMs: 120000 }
  );
}

export function fetchExecutiveSummary(filters, idToken) {
  return callGasAction(
    'getExecutiveSummary',
    {
      dateFrom: (filters && filters.dateFrom) || '',
      dateTo: (filters && filters.dateTo) || '',
      force: forceFlag_(filters)
    },
    idToken,
    { timeoutMs: 240000 }
  );
}

export function fetchAnalyticsSheetCacheStatus(idToken) {
  return callGasAction('getAnalyticsSheetCacheStatus', {}, idToken, { timeoutMs: 60000 });
}

export function ensureAnalyticsCacheSheets(idToken, opts) {
  return callGasAction(
    'ensureAnalyticsCacheSheets',
    {
      installTrigger: opts && opts.installTrigger ? '1' : ''
    },
    idToken,
    { timeoutMs: 120000 }
  );
}

export function refreshAnalyticsSheetCache(idToken, opts) {
  return callGasAction(
    'refreshAnalyticsSheetCache',
    {
      installTrigger: opts && opts.installTrigger ? '1' : ''
    },
    idToken,
    { timeoutMs: 330000 }
  );
}

export function installAnalyticsCacheTrigger(idToken) {
  return callGasAction('installAnalyticsCacheTrigger', {}, idToken, { timeoutMs: 60000 });
}
